import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The seam between LightOccluder2D and the 2D light pass. Each mounted occluder publishes its
 * local-space polygon with the group whose `matrixWorld` places it, read at pull time, so an
 * animated or instanced occluder stays right. With no provider, a registration is a silent no-op.
 */
import { createContext, useContext, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { visibleInTree } from '../visibleInTree';
export function createShadowCasterRegistry() {
    const entries = new Set();
    const listeners = new Set();
    let version = 0;
    /** Built by the first `casters()` after a change, dropped by `changed`. */
    let snapshot = null;
    const changed = () => {
        version++;
        snapshot = null;
        for (const listener of listeners)
            listener();
    };
    return {
        add(caster) {
            entries.add(caster);
            changed();
            return () => {
                if (entries.delete(caster))
                    changed();
            };
        },
        casters: () => (snapshot ??= [...entries]),
        version: () => version,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}
const ShadowCasterContext = createContext(null);
export function ShadowCasterProvider({ registry, children, }) {
    const own = useMemo(() => registry ?? createShadowCasterRegistry(), [registry]);
    return _jsx(ShadowCasterContext.Provider, { value: own, children: children });
}
/** Withholds the enclosing registry, so an occluder inside registers with no pass outside. */
export function NoShadowCasterRegistry({ children }) {
    return _jsx(ShadowCasterContext.Provider, { value: null, children: children });
}
/** The enclosing registry, or null when no pass is mounted above this subtree. */
function useShadowCasterRegistry() {
    return useContext(ShadowCasterContext);
}
/**
 * Publishes `caster` while it is non-null and a registry is in scope. Pass null while the occluder
 * has no resolved polygon or group, so the hook stays unconditional.
 */
export function useShadowCaster(caster) {
    const registry = useShadowCasterRegistry();
    useEffect(() => {
        if (!registry || !caster)
            return;
        return registry.add(caster);
    }, [registry, caster]);
}
/**
 * The visible casters in world-space `[ax,ay, bx,by, …]` for `buildShadowVolumes`. Omit the mask to
 * flatten once per frame and filter per light, since the flatten is the expensive half. World
 * matrices are refreshed: a `useFrame` pass runs before the renderer's `updateMatrixWorld`.
 */
export function worldShadowCasters(registry, shadowItemCullMask = 0xffffffff) {
    if (!registry)
        return [];
    const point = new THREE.Vector3();
    const out = [];
    for (const caster of registry.casters()) {
        // Godot's two gates, probed with `pnpm ref:godot --probe`: a mask of 2 against the light's
        // default 1 removes the shadow, and a hidden occluder casts nothing (VISIBILITY_CHANGED reads
        // `is_visible_in_tree()`). The CanvasItem `light_mask` is no gate.
        if ((caster.occluderLightMask & shadowItemCullMask) === 0)
            continue;
        if (!visibleInTree(caster.object))
            continue;
        const { segments } = caster;
        const points = Math.floor(segments.length / 3);
        if (points < 2)
            continue;
        caster.object.updateWorldMatrix(true, false);
        const { matrixWorld } = caster.object;
        const world = new Float32Array(points * 2);
        const local = emptyBox();
        for (let i = 0; i < points; i++) {
            const x = segments[i * 3];
            const y = segments[i * 3 + 1];
            expandBox(local, x, y);
            point.set(x, y, segments[i * 3 + 2]).applyMatrix4(matrixWorld);
            world[i * 2] = point.x;
            world[i * 2 + 1] = point.y;
        }
        out.push({
            segments: world,
            cullMode: caster.cullMode,
            occluderLightMask: caster.occluderLightMask,
            bounds: boxThrough(local, matrixWorld, point),
        });
    }
    return out;
}
function emptyBox() {
    return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}
function expandBox(box, x, y) {
    box.minX = Math.min(box.minX, x);
    box.maxX = Math.max(box.maxX, x);
    box.minY = Math.min(box.minY, y);
    box.maxY = Math.max(box.maxY, y);
}
/** `box`'s corners through `matrixWorld`, in order around it. `scratch` carries each corner. */
function boxThrough({ minX, minY, maxX, maxY }, matrixWorld, scratch) {
    const corner = (x, y) => {
        scratch.set(x, y, 0).applyMatrix4(matrixWorld);
        return { x: scratch.x, y: scratch.y };
    };
    return [corner(minX, minY), corner(maxX, minY), corner(maxX, maxY), corner(minX, maxY)];
}
//# sourceMappingURL=shadowCasterRegistry.js.map